import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
  HostBinding,
  ElementRef,
  inject,
  ViewChild,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Represents an action item in the action menu
 */
export interface ActionMenuItem {
  /** Unique identifier for the action */
  id: string;
  /** Display label for the action (optional for primary actions with icon) */
  label?: string;
  /** Icon class to display (optional) */
  icon?: string;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Function to determine if action is disabled based on item data */
  isDisabled?: (item: unknown) => boolean;
  /** Tooltip text for the action */
  tooltip?: string;
  /** Visual variant: 'default', 'danger', 'success' */
  variant?: 'default' | 'danger' | 'success';
}

/**
 * Configuration for the action menu component
 */
export interface ActionMenuConfig {
  /** Primary actions shown as buttons (e.g., Edit) */
  primaryActions?: ActionMenuItem[];
  /** Secondary actions shown in the dropdown menu */
  menuActions?: ActionMenuItem[];
  /** Label for the "more actions" button */
  moreActionsLabel?: string;
  /** Icon for the "more actions" button */
  moreActionsIcon?: string;
}

/**
 * Event emitted when an action is clicked
 */
export interface ActionMenuEvent<T = unknown> {
  /** The action that was clicked */
  action: ActionMenuItem;
  /** The data item associated with this row */
  item: T;
}

/**
 * ActionMenuComponent - A dropdown menu for table row actions
 *
 * Follows Atomic Design pattern as a molecule component.
 * Provides primary action buttons and a "more actions" dropdown menu.
 *
 * @example
 * ```html
 * <app-action-menu
 *   [config]="actionMenuConfig"
 *   [item]="rowData"
 *   (actionClick)="handleAction($event)"
 * ></app-action-menu>
 * ```
 */
@Component({
  selector: 'app-action-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './action-menu.component.html',
  styleUrls: ['./action-menu.component.scss']
})
export class ActionMenuComponent<T = unknown> implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly scrollHandler = this.onScroll.bind(this);
  private animationFrameId: number | null = null;

  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;

  /** Configuration for the action menu */
  @Input() config: ActionMenuConfig = {};

  /** The data item for this row */
  @Input() item!: T;

  /** Event emitted when any action is clicked */
  @Output() actionClick = new EventEmitter<ActionMenuEvent<T>>();

  /** Whether the dropdown menu is open */
  isMenuOpen = false;

  /** Bind 'menu-open' class to host when menu is open */
  @HostBinding('class.menu-open') get menuOpenClass(): boolean {
    return this.isMenuOpen;
  }

  /** Position for the dropdown menu (fixed position) */
  menuPosition = { top: 0, left: 0 };

  /** Check if there are primary actions to display */
  get hasPrimaryActions(): boolean {
    return (this.config.primaryActions?.length ?? 0) > 0;
  }

  /** Check if there are menu actions to display */
  get hasMenuActions(): boolean {
    return (this.config.menuActions?.length ?? 0) > 0;
  }

  /** Get the tooltip/title for the more actions button */
  get moreActionsButtonTitle(): string {
    return this.config.moreActionsLabel ?? 'Más acciones';
  }

  /** Check if custom more actions icon is configured */
  get hasCustomMoreActionsIcon(): boolean {
    return !!this.config.moreActionsIcon;
  }

  ngOnInit(): void {
    // Listen for scroll events on any scrollable container (capture phase)
    document.addEventListener('scroll', this.scrollHandler, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.scrollHandler, true);
    this.stopPositionTracking();
  }

  /**
   * Handle scroll event - update menu position when scrolling
   */
  private onScroll(): void {
    if (this.isMenuOpen) {
      this.ngZone.run(() => {
        this.calculateMenuPosition();
        this.cdr.detectChanges();
      });
    }
  }

  /**
   * Start tracking button position using requestAnimationFrame
   */
  private startPositionTracking(): void {
    const updatePosition = () => {
      if (this.isMenuOpen) {
        this.calculateMenuPosition();
        this.cdr.detectChanges();
        this.animationFrameId = requestAnimationFrame(updatePosition);
      }
    };
    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(updatePosition);
    });
  }

  /**
   * Stop tracking button position
   */
  private stopPositionTracking(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Toggle the dropdown menu visibility
   */
  toggleMenu(): void {
    if (!this.isMenuOpen) {
      this.calculateMenuPosition();
      this.isMenuOpen = true;
      this.startPositionTracking();
    } else {
      this.isMenuOpen = false;
      this.stopPositionTracking();
    }
  }

  /**
   * Calculate menu position based on trigger button (fixed positioning)
   */
  private calculateMenuPosition(): void {
    if (this.triggerButton) {
      const rect = this.triggerButton.nativeElement.getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + 4,
        left: rect.right - 192 // 12rem = 192px, align to right
      };

      // Adjust if menu would go off-screen to the left
      if (this.menuPosition.left < 8) {
        this.menuPosition.left = rect.left;
      }
    }
  }

  /**
   * Close the dropdown menu
   */
  closeMenu(): void {
    this.isMenuOpen = false;
    this.stopPositionTracking();
  }

  /**
   * Handle click on a primary action button
   */
  onPrimaryActionClick(action: ActionMenuItem): void {
    if (this.isActionDisabled(action)) {
      return;
    }
    this.actionClick.emit({ action, item: this.item });
  }

  /**
   * Handle click on a menu action
   */
  onMenuActionClick(action: ActionMenuItem): void {
    if (this.isActionDisabled(action)) {
      return;
    }
    this.closeMenu();
    this.actionClick.emit({ action, item: this.item });
  }

  /**
   * Check if an action is disabled
   */
  isActionDisabled(action: ActionMenuItem): boolean {
    if (action.disabled) {
      return true;
    }
    if (action.isDisabled) {
      return action.isDisabled(this.item);
    }
    return false;
  }

  /**
   * Get the title/tooltip for an action button
   */
  getActionTitle(action: ActionMenuItem): string {
    return action.tooltip ?? action.label ?? '';
  }

  /**
   * Close menu when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node;
    const nativeElement = this.elementRef.nativeElement as HTMLElement;
    if (!nativeElement.contains(target)) {
      this.closeMenu();
    }
  }

  /**
   * Close menu on escape key
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeMenu();
  }
}
